import { useState, useEffect, useCallback, useRef } from 'react';
import { LocalNotifications } from '@capacitor/local-notifications';
import { isNativeApp } from '../utils/platformUtils';
import storage from '../services/storageService';

/**
 * Hook para gerenciar notificações de agenda e missões
 * Suporta tanto Web Notifications (PWA/Browser) quanto Local Notifications (Capacitor/Android)
 * @param {Array} scheduledTrips - Lista de compromissos agendados
 * @param {string|number|null} selectedVehicleId - Veículo ativo; alertas de agenda
 *   de outros veículos são ocultados. Viagens legadas sem vehicleId sempre aparecem.
 * @returns {Object} - Notificações e funções de controle
 */
export default function useNotifications(scheduledTrips = [], selectedVehicleId = null) {
    const [notifications, setNotifications] = useState([]);
    // Initialize permission safely (Notification might not exist on iOS)
    const [permission, setPermission] = useState(() => {
        if (isNativeApp()) {
            return 'default'; // Será verificado de forma assíncrona
        }
        if ('Notification' in window) {
            return Notification.permission;
        }
        return 'default';
    });

    // Rastrear quais notificações já foram enviadas ao sistema para não repetir o som/banner
    const systemNotifiedRef = useRef(new Set());
    // Counter para IDs únicos de notificações nativas
    const notifIdCounterRef = useRef(1);

    const [dismissedIds, setDismissedIds] = useState(() => {
        return storage.getItemSync('rotaDoLucroNotificationsDismissed', []);
    });

    // Salvar IDs dispensados no storage
    useEffect(() => {
        storage.setItem('rotaDoLucroNotificationsDismissed', dismissedIds);
    }, [dismissedIds]);

    // Limpar IDs dispensados a cada 24 horas
    useEffect(() => {
        const lastClean = storage.getItemSync('rotaDoLucroNotificationsLastClean');
        const now = Date.now();
        const oneDay = 24 * 60 * 60 * 1000;

        if (!lastClean || now - parseInt(lastClean) > oneDay) {
            setDismissedIds([]);
            storage.setItem('rotaDoLucroNotificationsLastClean', now.toString());
            systemNotifiedRef.current.clear(); // Limpar cache de notificados do sistema também
        }
    }, []);

    // Verificar permissão nativa no Capacitor ao montar
    useEffect(() => {
        const checkNativePermission = async () => {
            if (isNativeApp()) {
                try {
                    const status = await LocalNotifications.checkPermissions();
                    setPermission(status.display === 'granted' ? 'granted' : 'default');
                } catch (e) {
                    console.log('Erro ao verificar permissão de notificações:', e);
                }
            }
        };
        checkNativePermission();
    }, []);

    // Função para solicitar permissão
    const requestPermission = useCallback(async () => {
        // Ambiente nativo (Capacitor/Android)
        if (isNativeApp()) {
            try {
                const status = await LocalNotifications.requestPermissions();
                const granted = status.display === 'granted';
                setPermission(granted ? 'granted' : 'denied');

                if (granted) {
                    // Enviar notificação de teste
                    await LocalNotifications.schedule({
                        notifications: [{
                            id: 0,
                            title: 'Notificações Ativadas',
                            body: 'Agora você receberá alertas de agenda e missões!',
                            smallIcon: 'ic_stat_meupainel_notification',
                            largeIcon: 'ic_launcher',
                        }]
                    });
                }
                return granted;
            } catch (error) {
                console.error('Erro ao solicitar permissão nativa:', error);
                return false;
            }
        }

        // Ambiente Web/PWA
        if (!('Notification' in window)) {
            console.log('Este navegador não suporta notificações desktop');
            return false;
        }

        try {
            const result = await Notification.requestPermission();
            setPermission(result);
            if (result === 'granted') {
                try {
                    new Notification('Notificações Ativadas', {
                        body: 'Agora você receberá alertas de agenda e missões mesmo com o app minimizado!',
                        icon: '/icon-192.png'
                    });
                } catch (e) {
                    console.log('Notification constructor not supported');
                }
            }
            return result === 'granted';
        } catch (error) {
            console.error('Erro ao solicitar permissão:', error);
            return false;
        }
    }, []);

    // Função interna para disparar notificação do sistema
    const sendSystemNotification = useCallback(async (title, body, tag) => {
        // Evitar notificações duplicadas
        if (systemNotifiedRef.current.has(tag)) return;

        // Ambiente nativo (Capacitor/Android)
        if (isNativeApp()) {
            try {
                const permResult = await LocalNotifications.checkPermissions();
                if (permResult.display === 'granted') {
                    const notifId = notifIdCounterRef.current++;
                    await LocalNotifications.schedule({
                        notifications: [{
                            id: notifId,
                            title,
                            body,
                            smallIcon: 'ic_stat_meupainel_notification',
                            largeIcon: 'ic_launcher',
                            channelId: 'meupainel-notifications',
                            extra: { tag }
                        }]
                    });
                    systemNotifiedRef.current.add(tag);
                }
            } catch (e) {
                console.error('Erro ao enviar notificação nativa:', e);
            }
            return;
        }

        // Ambiente Web/PWA
        if (!('Notification' in window)) return;

        if (Notification.permission === 'granted') {
            try {
                // Tenta usar ServiceWorker se disponível (melhor para mobile/PWA)
                if (navigator.serviceWorker && navigator.serviceWorker.ready) {
                    navigator.serviceWorker.ready.then(registration => {
                        registration.showNotification(title, {
                            body,
                            icon: '/icon-192.png',
                            badge: '/favicon.png',
                            tag,
                            vibrate: [200, 100, 200]
                        });
                    });
                } else {
                    // Fallback para Notification API padrão
                    new Notification(title, {
                        body,
                        icon: '/icon-192.png',
                        tag // tag evita duplicatas nativas do OS
                    });
                }
                systemNotifiedRef.current.add(tag);
            } catch (e) {
                console.error('Erro ao enviar notificação do sistema:', e);
            }
        }
    }, []);

    // Função para atualizar notificação do cronômetro (barra do sistema)
    const updateTimerNotification = useCallback(async (isActive, isPaused, timeDisplay, startTime) => {
        if (!isNativeApp()) return; // Só para Mobile por enquanto

        try {
            // Registrar os tipos de ação se ainda não foram (Capacitor gerencia isso internamente)
            await LocalNotifications.registerActionTypes({
                types: [
                    {
                        id: 'TIMER_CONTROLS',
                        actions: [
                            {
                                id: 'pause_resume',
                                title: isPaused ? 'Retomar ▶️' : 'Pausar ⏸️',
                                foreground: false // Pode rodar em background
                            },
                            {
                                id: 'stop',
                                title: 'Parar ⏹️',
                                foreground: true // Precisa abrir o app para confirmação
                            }
                        ]
                    }
                ]
            });

            const status = isPaused ? 'Pausado ⏸️' : (isActive ? 'Ativo ⏱️' : 'Parado');
            if (!isActive && !isPaused) {
                // Cancelar notificação se o cronômetro parar
                await LocalNotifications.cancel({ notifications: [{ id: 999 }] });
                return;
            }

            await LocalNotifications.schedule({
                notifications: [{
                    id: 999, // ID fixo para o cronômetro
                    title: `Cronômetro: ${status}`,
                    body: isPaused ? `Pausado em: ${timeDisplay}` : `Toque para abrir o app`,
                    smallIcon: 'ic_stat_meupainel_notification',
                    ongoing: true, // Tenta manter a notificação fixa (Android)
                    autoCancel: false,
                    channelId: 'meupainel-timer',
                    actionTypeId: 'TIMER_CONTROLS', // Vincula os botões
                    // Novos campos para cronômetro nativo
                    chronometer: !isPaused && isActive,
                    at: startTime ? new Date(startTime) : undefined
                }]
            });
        } catch (e) {
            console.error('Erro ao atualizar notificação de cronômetro:', e);
        }
    }, []);

    const checkNotifications = useCallback(() => {
        const now = new Date();
        const newNotifications = [];

        // === VERIFICAR COMPROMISSOS DA AGENDA ===
        // Mostra apenas compromissos do veículo selecionado (viagens legadas
        // sem vehicleId continuam aparecendo para não perder nenhum alerta).
        scheduledTrips
            .filter(trip => trip.status === 'pending')
            .filter(trip => !trip.vehicleId || selectedVehicleId == null || String(trip.vehicleId) === String(selectedVehicleId))
            .forEach(trip => {
                if (!trip.date || !trip.time) return;

                // Construir data/hora do compromisso
                const [year, month, day] = trip.date.split('-').map(Number);
                const [hour, minute] = trip.time.split(':').map(Number);
                const tripDateTime = new Date(year, month - 1, day, hour, minute);

                const diffMs = tripDateTime.getTime() - now.getTime();
                const diffMinutes = diffMs / (1000 * 60);

                // Notificar se está entre 0 e 120 minutos
                if (diffMinutes > 0 && diffMinutes <= 120) {
                    const notifId = `agenda-${trip.id}`;

                    // Se não foi dispensado manualmente
                    if (!dismissedIds.includes(notifId)) {
                        let message = '';
                        let urgency = 'info';

                        if (diffMinutes <= 30) {
                            message = `em ${Math.round(diffMinutes)} minutos`;
                            urgency = 'warning';
                            // Disparar sistema (reforço de urgência)
                            sendSystemNotification(
                                'Compromisso Próximo!',
                                `Viagem com ${trip.client} começa em ${Math.round(diffMinutes)} min`,
                                notifId
                            );
                        } else if (diffMinutes <= 60) {
                            message = `em ${Math.round(diffMinutes)} minutos`;
                            urgency = 'info';
                            sendSystemNotification(
                                'Compromisso na Agenda',
                                `Viagem com ${trip.client} começa em 1 hora`,
                                notifId
                            );
                        } else {
                            const hours = Math.floor(diffMinutes / 60);
                            const mins = Math.round(diffMinutes % 60);
                            message = `em ${hours}h${mins > 0 ? ` ${mins}min` : ''}`;
                            urgency = 'info';
                        }

                        newNotifications.push({
                            id: notifId,
                            type: 'agenda',
                            title: `Compromisso: ${trip.client}`,
                            message: message,
                            urgency: urgency,
                            data: trip
                        });
                    }
                }
            });

        // === VERIFICAR MISSÕES ===
        try {
            const missions = storage.getItemSync('mission_tracker_storage_v2');
            if (missions) {

                missions.forEach(mission => {
                    if (!mission.startDate || !mission.deadline) return;

                    const startDate = new Date(mission.startDate);
                    const deadline = new Date(mission.deadline);
                    const nowTime = now.getTime();

                    // Verificar se missão está completa
                    const tierIndex = mission.tiers.findIndex(t => mission.currentTrips < t.targetTrips);
                    const isComplete = mission.tiers.length > 0 && tierIndex === -1;

                    if (isComplete) return; // Ignorar missões completas

                    // Missão prestes a COMEÇAR (próximos 60 minutos)
                    const startDiffMs = startDate.getTime() - nowTime;
                    const startDiffMinutes = startDiffMs / (1000 * 60);

                    if (startDiffMinutes > 0 && startDiffMinutes <= 60) {
                        const notifId = `mission-start-${mission.id}`;
                        if (!dismissedIds.includes(notifId)) {
                            const msg = `começa em ${Math.round(startDiffMinutes)} minutos`;
                            sendSystemNotification(
                                `Missão ${mission.platform.toUpperCase()} Começando!`,
                                `${mission.title} ${msg}`,
                                notifId
                            );

                            newNotifications.push({
                                id: notifId,
                                type: 'mission-start',
                                title: `Missão ${mission.platform.toUpperCase()}: ${mission.title}`,
                                message: msg,
                                urgency: 'info',
                                data: mission
                            });
                        }
                    }

                    // Missão prestes a TERMINAR (próximas 2 horas)
                    const endDiffMs = deadline.getTime() - nowTime;
                    const endDiffMinutes = endDiffMs / (1000 * 60);

                    if (endDiffMinutes > 0 && endDiffMinutes <= 120 && nowTime >= startDate.getTime()) {
                        const notifId = `mission-end-${mission.id}`;
                        if (!dismissedIds.includes(notifId)) {
                            const remaining = mission.tiers[tierIndex]?.targetTrips - mission.currentTrips || 0;
                            let urgency = 'warning';
                            let timeStr = '';

                            if (endDiffMinutes <= 30) {
                                urgency = 'danger';
                                timeStr = `${Math.round(endDiffMinutes)} minutos`;
                                sendSystemNotification(
                                    'Corre! Missão Acabando!',
                                    `${mission.title} termina em ${timeStr}. Faltam ${remaining} viagens!`,
                                    notifId + '-urgent' // Tag diferente para forçar novo alerta se já alertou antes
                                );
                            } else if (endDiffMinutes <= 60) {
                                timeStr = `${Math.round(endDiffMinutes)} minutos`;
                                sendSystemNotification(
                                    'Missão Terminando',
                                    `${mission.title} termina em ${timeStr}. Faltam ${remaining} viagens.`,
                                    notifId
                                );
                            } else {
                                const hours = Math.floor(endDiffMinutes / 60);
                                const mins = Math.round(endDiffMinutes % 60);
                                timeStr = `${hours}h${mins > 0 ? ` ${mins}min` : ''}`;
                            }

                            newNotifications.push({
                                id: notifId,
                                type: 'mission-end',
                                title: `Missão ${mission.platform.toUpperCase()}: ${mission.title}`,
                                message: `termina em ${timeStr} - faltam ${remaining} viagens`,
                                urgency: urgency,
                                data: mission
                            });
                        }
                    }
                });
            }
        } catch (e) {
            console.error('Erro ao verificar missões para notificações:', e);
        }

        // Só atualiza o estado se houver mudança real nas notificações
        setNotifications(prev => {
            if (JSON.stringify(prev) === JSON.stringify(newNotifications)) return prev;
            return newNotifications;
        });
    }, [scheduledTrips, selectedVehicleId, dismissedIds, sendSystemNotification]);

    // Verificar notificações a cada minuto
    useEffect(() => {
        checkNotifications();
        const interval = setInterval(checkNotifications, 60000);
        return () => clearInterval(interval);
    }, [checkNotifications]);

    // Função para dispensar uma notificação
    const dismissNotification = useCallback((notifId) => {
        setDismissedIds(prev => [...prev, notifId]);
    }, []);

    // Função para dispensar todas as notificações
    const dismissAll = useCallback(() => {
        const ids = notifications.map(n => n.id);
        setDismissedIds(prev => [...prev, ...ids]);
    }, [notifications]);

    return {
        notifications,
        permission,
        requestPermission,
        dismissNotification,
        dismissAll,
        updateTimerNotification,
        sendSystemNotification,
        hasNotifications: notifications.length > 0
    };
}
